import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  Ticket, Plus, Clock, AlertCircle, CheckCircle, 
  MessageCircle, Search, Filter, User, Calendar,
  ChevronDown, ChevronUp, Send
} from 'lucide-react';

const Tickets = () => {
  const { currentUser } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [filter, setFilter] = useState('all');

  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'medium',
    category: 'general'
  });

  const priorityColors = {
    low: 'bg-green-900/30 text-green-400 border-green-800',
    medium: 'bg-yellow-900/30 text-yellow-400 border-yellow-800',
    high: 'bg-red-900/30 text-red-400 border-red-800'
  };

  const statusColors = {
    open: 'bg-blue-900/30 text-blue-400 border-blue-800',
    in_progress: 'bg-orange-900/30 text-orange-400 border-orange-800',
    resolved: 'bg-green-900/30 text-green-400 border-green-800',
    closed: 'bg-slate-700 text-slate-400 border-slate-600'
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/tickets', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      } else {
        throw new Error('Failed to fetch tickets');
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setError('Failed to load tickets. The ticketing system may not be fully set up yet.');
      // Show mock data for demonstration
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTicket = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/tickets', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newTicket,
          businessUnitId: currentUser.businessUnit?.id
        })
      });

      if (response.ok) {
        setSuccess('Ticket submitted successfully! You will receive updates via email.');
        setNewTicket({ title: '', description: '', priority: 'medium', category: 'general' });
        setShowNewTicketForm(false);
        fetchTickets();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        throw new Error('Failed to submit ticket');
      }
    } catch (error) {
      console.error('Submit error:', error);
      setError('Failed to submit ticket. The ticketing system may not be fully set up yet.');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      open: 'Open',
      in_progress: 'In Progress',
      resolved: 'Resolved',
      closed: 'Closed'
    };
    return labels[status] || status;
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filter === 'all') return true;
    return ticket.status === filter;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-700 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1,2,3].map(i => (
              <div key={i} className="h-24 bg-slate-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">Support Tickets</h1>
          <p className="text-slate-400 mt-1">Submit and track IT support requests</p>
        </div>
        <button
          onClick={() => setShowNewTicketForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Ticket
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-900/30 border border-red-800 rounded-lg flex items-center gap-3 text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-900/30 border border-green-800 rounded-lg flex items-center gap-3 text-green-400">
          <CheckCircle className="w-5 h-5" />
          <span>{success}</span>
        </div>
      )}

      {/* New Ticket Form Modal */}
      {showNewTicketForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 w-full max-w-2xl mx-4">
            <h2 className="text-2xl font-bold text-slate-100 mb-4">Submit New Ticket</h2>
            
            <form onSubmit={handleSubmitTicket}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Priority
                  </label>
                  <select
                    value={newTicket.priority}
                    onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-300 text-sm font-medium mb-2">
                    Category
                  </label>
                  <select
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                  >
                    <option value="general">General Support</option>
                    <option value="hardware">Hardware Issue</option>
                    <option value="software">Software Issue</option>
                    <option value="network">Network/Connectivity</option>
                    <option value="access">Access Request</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-slate-300 text-sm font-medium mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={newTicket.title}
                  onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                  placeholder="Brief description of the issue"
                  required
                />
              </div>

              <div className="mb-6">
                <label className="block text-slate-300 text-sm font-medium mb-2">
                  Description *
                </label>
                <textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200 h-32"
                  placeholder="Detailed description of the issue, including steps to reproduce, error messages, etc."
                  required
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowNewTicketForm(false)}
                  className="px-4 py-2 text-slate-400 hover:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  {submitting ? 'Submitting...' : 'Submit Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Filter and Search */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-slate-700 text-slate-200 border border-slate-600 rounded-lg px-3 py-2"
          >
            <option value="all">All Tickets</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Tickets List */}
      <div className="bg-slate-800 rounded-lg border border-slate-700">
        <div className="p-6 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-slate-100">
            Your Tickets ({filteredTickets.length})
          </h2>
        </div>

        {filteredTickets.length === 0 ? (
          <div className="p-8 text-center">
            <Ticket className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-300 mb-2">
              {filter === 'all' ? 'No tickets found' : `No ${filter.replace('_', ' ')} tickets`}
            </h3>
            <p className="text-slate-400 mb-4">
              {filter === 'all' 
                ? 'Submit your first support ticket to get help from our IT team'
                : 'No tickets match the current filter'
              }
            </p>
            {filter === 'all' && (
              <button
                onClick={() => setShowNewTicketForm(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Submit Your First Ticket
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-slate-700">
            {filteredTickets.map((ticket) => (
              <div key={ticket.id} className="p-4">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedTicket(expandedTicket === ticket.id ? null : ticket.id)}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <Ticket className="w-5 h-5 text-slate-400" />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-slate-200 font-medium truncate">
                        #{ticket.id} - {ticket.title}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-slate-400 mt-1">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full border ${priorityColors[ticket.priority]}`}>
                          {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full border ${statusColors[ticket.status]}`}>
                          {getStatusLabel(ticket.status)}
                        </span>
                      </div>
                    </div>
                  </div>
                  {expandedTicket === ticket.id ? 
                    <ChevronUp className="w-4 h-4 text-slate-400" /> : 
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  }
                </div>

                {expandedTicket === ticket.id && (
                  <div className="mt-4 pl-8 border-l-2 border-slate-700">
                    <div className="bg-slate-700/30 rounded-lg p-4 mb-4">
                      <h4 className="text-slate-300 font-medium mb-2">Description</h4>
                      <p className="text-slate-400 whitespace-pre-wrap">{ticket.description}</p>
                    </div>

                    {ticket.responses && ticket.responses.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="text-slate-300 font-medium flex items-center gap-2">
                          <MessageCircle className="w-4 h-4" />
                          Responses ({ticket.responses.length})
                        </h4>
                        {ticket.responses.map((response, index) => (
                          <div key={index} className="bg-slate-700/20 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <User className="w-3 h-3 text-slate-400" />
                              <span className="text-slate-300 text-sm font-medium">{response.from}</span>
                              <span className="text-slate-500 text-xs">{new Date(response.timestamp).toLocaleString()}</span>
                            </div>
                            <p className="text-slate-400 text-sm">{response.message}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help Text */}
      <div className="mt-6 p-4 bg-slate-700/30 rounded-lg border border-slate-600">
        <p className="text-slate-400 text-sm">
          <strong>Need help?</strong> Submit a ticket and our IT team will respond within 24 hours during business days. 
          For urgent issues, please call the IT help desk directly.
        </p>
      </div>
    </div>
  );
};

export default Tickets;